import { useState } from "react";
import { Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

// Case « recevoir les étapes de ma commande par texto » + code à 6 chiffres
// (demande du propriétaire, 2026-10-30). Le client coche, reçoit un code par
// texto, le confirme — seul un numéro VÉRIFIÉ reçoit ensuite les 4 liens
// (soumission, approbation, paiement, matrice).
export function SmsEtapesBox({ phone, quoteToken, userId, verified, onVerified }) {
  const { lang } = useLang();
  const [on, setOn] = useState(!!verified);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(verified ? "done" : "idle"); // idle | sent | done

  const sendCode = async () => {
    if (!/^\d{10}$/.test((phone || "").replace(/\D/g, "").replace(/^1/, ""))) {
      toast.error(lang === "fr" ? "Entrez un numéro de cellulaire valide (10 chiffres)." : "Enter a valid 10-digit cell number.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/sms/send-code", { phone, quote_token: quoteToken, user_id: userId });
      setOn(true);
      setStep("sent");
      toast.success(lang === "fr" ? "Code envoyé par texto — entrez-le pour confirmer." : "Code sent by text — enter it to confirm.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/sms/verify-code", { phone, code, quote_token: quoteToken, user_id: userId });
      setStep("done");
      setOn(true);
      onVerified && onVerified();
      toast.success(lang === "fr" ? "Numéro confirmé — vous recevrez les étapes par texto." : "Number confirmed — you'll get order steps by text.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="sms-etapes-box" className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
      <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="sms-etapes-case">
        <input type="checkbox" checked={on} disabled={busy || step === "done"}
          onChange={(e) => { const v = e.target.checked; if (v) sendCode(); else { setOn(false); setStep("idle"); } }}
          className="w-4 h-4 accent-emerald-500" />
        <span className="text-sm text-ivory/85 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-emerald-400" />
          {lang === "fr" ? "Recevoir les étapes de ma commande par texto" : "Get my order steps by text"}
        </span>
      </label>
      {on && step === "sent" && (
        <div className="flex items-center gap-2 mt-3" data-testid="sms-etapes-code-row">
          <input data-testid="sms-etapes-code" value={code} maxLength={6} inputMode="numeric"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder={lang === "fr" ? "Code à 6 chiffres" : "6-digit code"}
            className="w-32 bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-center tracking-widest focus:border-emerald-400 focus:outline-none" />
          <button type="button" onClick={verify} disabled={busy || code.length !== 6}
            data-testid="sms-etapes-verify"
            className="px-4 py-2 rounded-full text-xs font-semibold bg-emerald-500 text-ink hover:bg-emerald-400 disabled:opacity-50 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "fr" ? "Confirmer" : "Confirm")}
          </button>
        </div>
      )}
      {step === "done" && (
        <p className="text-xs text-emerald-300 mt-2" data-testid="sms-etapes-ok">
          {lang === "fr" ? "Numéro confirmé — les liens (soumission, approbation, paiement, matrice) vous seront textés." : "Number confirmed — the links will be texted to you."}
        </p>
      )}
    </div>
  );
}
